import React from 'react';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  maxFieldArrayCheckbox,
  composeValidators,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
import {
  AR_TEACHING_CATEGORY_LABELS,
  normalizeCategoryLookupKey,
} from '../../util/helpersFunction';
// Import shared components
import { FieldCheckboxGroup, FieldSelect, FieldTextInput, FieldBoolean } from '../../components';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;
const getTeachingCategoriesLabel = intl =>
  String(intl?.locale || '').toLowerCase().startsWith('ar')
    ? 'فئات التدريس'
    : intl.formatMessage({
        id: 'CustomExtendedDataField.teachingCategoriesLabel',
        defaultMessage: 'Teaching Categories',
      });

const isTeachingCategoryOptionSet = enumOptions =>
  Array.isArray(enumOptions) &&
  enumOptions.some(option => {
    const optionKey = normalizeCategoryLookupKey(option?.option);
    const labelKey = normalizeCategoryLookupKey(option?.label);
    return !!(AR_TEACHING_CATEGORY_LABELS[optionKey] || AR_TEACHING_CATEGORY_LABELS[labelKey]);
  });

const localizeTeachingCategoryOptionLabel = (option, locale) => {
  const isArabicLocale = String(locale || '').toLowerCase().startsWith('ar');
  if (!isArabicLocale) {
    return option?.label;
  }

  const byOption = AR_TEACHING_CATEGORY_LABELS[normalizeCategoryLookupKey(option?.option)];
  if (byOption) {
    return byOption;
  }

  const byLabel = AR_TEACHING_CATEGORY_LABELS[normalizeCategoryLookupKey(option?.label)];
  return byLabel || option?.label;
};

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });
  const filterOptions = createFilterOptions(enumOptions);

  const label = isTeachingCategoriesField ? getTeachingCategoriesLabel(intl) : getLabel(fieldConfig);

  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => {
        const key = optionConfig.key;
        return (
          <option key={key} value={key}>
            {optionConfig.label}
          </option>
        );
      })}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage, maxSelections, maxSelectionsMessage } = saveConfig || {};
  const normalizedKey = String(fieldConfig?.key || '')
    .trim()
    .toLowerCase()
    .replace(/[_-\s]/g, '');
  const normalizedLabel = String(getLabel(fieldConfig) || '')
    .trim()
    .toLowerCase();
  const normalizedName = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[_-\s]/g, '');
  const isTeachingCategoriesField =
    normalizedKey === 'teachingcategories' ||
    normalizedLabel === 'teaching categories' ||
    (normalizedName.includes('teaching') && normalizedName.includes('categor')) ||
    isTeachingCategoryOptionSet(enumOptions);
  const parsedMaxSelections = Number.parseInt(maxSelections, 10);
  const maxSelected =
    Number.isInteger(parsedMaxSelections) && parsedMaxSelections > 0
      ? parsedMaxSelections
      : isTeachingCategoriesField
      ? 3
      : null;
  const label = getLabel(fieldConfig);
  const requiredValidator = isRequired
    ? nonEmptyArray(requiredMessage || defaultRequiredMessage)
    : null;
  const defaultMaxMessage = isTeachingCategoriesField
    ? intl.formatMessage({
        id: 'CustomExtendedDataField.maxTeachingCategories',
        defaultMessage: 'You can select up to 3 categories only.',
      })
    : intl.formatMessage(
        {
          id: 'CustomExtendedDataField.maxSelections',
          defaultMessage: 'You can select up to {max} options only.',
        },
        { max: maxSelected }
      );
  const maxSelectedMessage = maxSelectionsMessage || defaultMaxMessage;
  const maxSelectedValidator = maxSelected
    ? maxFieldArrayCheckbox(maxSelectedMessage, maxSelected)
    : null;
  const validateMaybe =
    requiredValidator && maxSelectedValidator
      ? { validate: composeValidators(requiredValidator, maxSelectedValidator) }
      : requiredValidator
      ? { validate: requiredValidator }
      : maxSelectedValidator
      ? { validate: maxSelectedValidator }
      : {};
  const localizedEnumOptions = isTeachingCategoriesField
    ? enumOptions.map(option => ({
        ...option,
        label: localizeTeachingCategoryOptionLabel(option, intl?.locale),
      }))
    : enumOptions;

  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      options={createFilterOptions(localizedEnumOptions)}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="textarea"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldMultiEnum, props)
    : schemaType === SCHEMA_TYPE_TEXT
    ? renderFieldComponent(CustomFieldText, props)
    : schemaType === SCHEMA_TYPE_LONG
    ? renderFieldComponent(CustomFieldLong, props)
    : schemaType === SCHEMA_TYPE_BOOLEAN
    ? renderFieldComponent(CustomFieldBoolean, props)
    : schemaType === SCHEMA_TYPE_YOUTUBE
    ? renderFieldComponent(CustomFieldYoutube, props)
    : null;
};

export default CustomExtendedDataField;
